<script setup lang="ts">
import PdsButton from '@/components/pds/PdsButton.vue';
import PdsCheck from '@/components/pds/PdsCheck.vue';
import { computed, onMounted, ref } from 'vue';
import type { StockLine, StockPart } from '../stock';
import { usePdsOrdersStore } from '../stores/orders.store';
import { usePdsStockStore } from '../stores/stock.store';

// `lineId` est celui de la ligne à préparer : le volet relit l'état à chaque cochage, car cocher une commande fait
// passer sa part de cette ligne à la ligne « déjà préparé » du même produit.
const props = defineProps<{ lineId: string; zone: string }>();
const emit = defineEmits<{ close: [] }>();

const stock = usePdsStockStore();
const orders = usePdsOrdersStore();
const panel = ref<HTMLElement>();

const lines = computed(() => stock.zones.flatMap((zone) => zone.lines));
// Ouvert depuis une ligne à préparer ou depuis « Déjà préparé » : dans les deux cas, on retrouve la paire.
const todoId = computed(() => props.lineId.replace(/\|true$/, '|false'));
const todo = computed(() => lines.value.find((l) => l.id === todoId.value));
const twin = computed(() =>
  lines.value.find((l) => l.id === todoId.value.replace(/\|false$/, '|true')),
);
const shown = computed(() => todo.value ?? twin.value);

interface Row {
  part: StockPart;
  prepared: boolean;
  orderId: string;
  itemIds: string[];
  // Une commande se coche quand elle est en préparation, pas avant (le serveur la refuse) ni une fois prête ou remise.
  locked: boolean;
  hint?: string;
}
const rowsOf = (line: StockLine | undefined, prepared: boolean): Row[] =>
  (line?.parts ?? []).map((part) => {
    const source = line?.sources?.[part.number];
    const stage = source && orders.byId(source.orderId)?.stage;
    const locked = !source || stage !== 'preparing';
    return {
      part,
      prepared,
      orderId: source?.orderId ?? '',
      itemIds: source?.itemIds ?? [],
      locked,
      hint: !locked
        ? undefined
        : stage === 'new'
          ? 'À accepter d’abord'
          : stage === 'handed'
            ? 'Remise au client'
            : 'Déjà prête',
    };
  });
const rows = computed<Row[]>(() => [
  ...rowsOf(todo.value, false),
  ...rowsOf(twin.value, true),
]);
const preparedCount = computed(
  () => rows.value.filter((r) => r.prepared).length,
);
const canMarkAll = computed(() =>
  rows.value.some((r) => !r.prepared && !r.locked),
);

const toggle = (row: Row) => {
  if (row.locked) return;
  orders.setItemsPrepared(row.orderId, row.itemIds, !row.prepared);
};
const markAll = () => {
  rows.value
    .filter((row) => !row.prepared && !row.locked)
    .forEach((row) => orders.setItemsPrepared(row.orderId, row.itemIds, true));
};

onMounted(() => panel.value?.focus());
</script>

<template>
  <Teleport to="body">
    <div
      class="font-pds bg-voile fixed inset-0 z-50"
      @mousedown.self="emit('close')"
      @keydown.esc="emit('close')"
    >
      <div
        ref="panel"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
        class="bg-creux text-encre absolute inset-y-0 right-0 flex w-[560px] max-w-full flex-col gap-3 p-5 outline-none"
      >
        <header v-if="shown">
          <h2 class="text-[24px] leading-[1.15] font-bold tracking-[-0.2px]">
            {{ todo?.quantity ?? 0 }} × {{ shown.name }}
          </h2>
          <p class="text-encre-douce pt-[6px] text-[14px] leading-[1.4]">
            {{ zone }} · {{ rows.length }} commande{{
              rows.length > 1 ? 's' : ''
            }}
            · {{ preparedCount }} préparée{{ preparedCount > 1 ? 's' : '' }} ·
            par heure de retrait
          </p>
        </header>
        <p
          class="text-encre-douce pt-2 text-[12px] leading-[14px] font-semibold tracking-[0.9px] uppercase"
        >
          Cocher ce qui est préparé
        </p>
        <ul
          class="no-scrollbar bg-carte min-h-0 overflow-y-auto rounded-[24px]"
        >
          <li
            v-for="row in rows"
            :key="`${row.part.number}-${row.prepared}`"
            class="border-filet-doux border-t first:border-t-0"
          >
            <button
              type="button"
              class="flex min-h-[49px] w-full items-center gap-3 px-4 py-3 text-left disabled:cursor-not-allowed disabled:opacity-60"
              :class="!row.locked && 'cursor-pointer'"
              :aria-pressed="row.prepared"
              :disabled="row.locked"
              @click="toggle(row)"
            >
              <PdsCheck :checked="row.prepared" />
              <span
                class="text-encre-douce text-[18px] leading-[22px] font-bold"
              >
                {{ row.part.quantity }}×
              </span>
              <span class="text-[18px] leading-[22px] font-bold">
                #{{ row.part.ref }}
              </span>
              <span
                class="text-encre-douce min-w-0 flex-1 truncate text-[14px]"
              >
                {{ row.hint ?? row.part.label }}
              </span>
              <span class="text-[14px] leading-5 font-semibold">
                {{ shown?.dueAt }}
              </span>
            </button>
          </li>
        </ul>
        <PdsButton class="mt-auto" :disabled="!canMarkAll" @click="markAll"
          >Tout marquer réparti</PdsButton
        >
      </div>
    </div>
  </Teleport>
</template>
